import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { canView, isGlobalSuperAdmin } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Clock,
  Bell,
  AlertTriangle,
  User,
  Search,
  FileText,
  BarChart3,
  ArrowRight,
  Sparkles,
  Library,
  Monitor,
} from "lucide-react";
import { LibraryCardDisplay } from "@/components/LibraryCardDisplay";
import { PatronOnboardingTour } from "@/components/patron/PatronOnboardingTour";
import api from "@/lib/api";

interface DashboardStats {
  checkedOut: number;
  overdue: number;
  activeHolds: number;
  pendingFinesAmount: number;
  pendingFinesCount: number;
  pendingInvoices: number;
  booksThisYear: number;
  totalBorrowed: number;
  memberSince: string | null;
  membershipStatus: string;
}

export function PatronDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const perms = user?.permissions || [];

  const has = (perm: string) => isGlobalSuperAdmin(user) || canView(perm, perms);

  const showCatalog = has("catalog.view") || has("catalog.list");
  const showHolds = has("catalog.requestHold") || has("catalog.reserve");
  const showFines = has("fines.viewOwn") || has("fines.payOnline");
  const showDigital = has("digital.view") || has("digital.download");
  const showHistory = has("circulation.viewHistory") || has("account.viewHistory");
  const showNotifications = has("notifications.view");
  const showStats = has("dashboard.viewStats");

  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const res = await api.get("/circulation/my/dashboard-stats", {
          signal: controller.signal,
        });
        if (!controller.signal.aborted) setStats(res.data);
      } catch (err: any) {
        if (err.name === "CanceledError") return;
        // Retry once after delay (handles mobile cookie timing)
        if (!controller.signal.aborted) {
          await new Promise((r) => setTimeout(r, 400));
          try {
            const retry = await api.get("/circulation/my/dashboard-stats", {
              signal: controller.signal,
            });
            if (!controller.signal.aborted) setStats(retry.data);
          } catch (e: any) {
            if (e.name !== "CanceledError") console.error("Failed to load dashboard stats:", e);
          }
        }
      }
    };
    load();
    return () => controller.abort();
  }, []);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const formatMemberSince = (dateStr: string | null | undefined) => {
    if (!dateStr) return "--";
    return new Date(dateStr).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-5">
      <PatronOnboardingTour />
      {/* Header */}
      <div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
        data-patron-tour="welcome"
      >
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {greeting()}
            {user?.firstName ? `, ${user.firstName}` : ""}!
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage your library account and books</p>
        </div>
        {showCatalog && (
          <Button size="sm" onClick={() => navigate("/catalog")}>
            <Search className="w-4 h-4 mr-1.5" />
            Search Catalog
          </Button>
        )}
      </div>

      {/* Library Card */}
      <LibraryCardDisplay />

      {/* Stats Strip */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
          <StripMetric
            label="Checked Out"
            value={stats ? String(stats.checkedOut + stats.overdue) : "..."}
            icon={<BookOpen className="w-4 h-4 text-primary" />}
            onClick={() => navigate("/my-books")}
            alert={stats && stats.overdue > 0 ? `${stats.overdue} overdue` : undefined}
          />
          <StripMetric
            label="Active Holds"
            value={stats ? String(stats.activeHolds) : "..."}
            icon={<Clock className="w-4 h-4 text-primary" />}
            onClick={showHolds ? () => navigate("/my-holds") : undefined}
          />
          <StripMetric
            label="Invoices"
            value={stats ? String(stats.pendingInvoices) : "..."}
            icon={<FileText className="w-4 h-4 text-primary" />}
            onClick={() => navigate("/invoices")}
          />
          <StripMetric
            label="Fines"
            value={stats ? `₦${stats.pendingFinesAmount.toFixed(2)}` : "..."}
            icon={<AlertTriangle className="w-4 h-4 text-amber-600" />}
            onClick={showFines ? () => navigate("/my-fines") : undefined}
            alert={
              stats && stats.pendingFinesCount > 0
                ? `${stats.pendingFinesCount} pending`
                : undefined
            }
          />
          <StripMetric
            label="Notifications"
            value="0"
            icon={<Bell className="w-4 h-4 text-primary" />}
            onClick={showNotifications ? () => navigate("/notifications") : undefined}
          />
        </div>
      </div>

      {/* Main Grid — 3/5 + 2/5 */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Left column */}
        <div className="lg:col-span-3 space-y-5">
          {/* Currently Checked Out — always visible */}
          <Card className="border border-slate-200" data-patron-tour="checkouts">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-base font-semibold text-slate-900">
                  My Checkouts
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/my-books")}
                  className="text-xs"
                >
                  View All
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {stats && stats.checkedOut + stats.overdue > 0 ? (
                <div className="text-center py-6">
                  <BookOpen className="w-8 h-8 mx-auto mb-2 text-primary/60" />
                  <p className="text-sm font-medium text-slate-900">
                    You have {stats.checkedOut + stats.overdue} item
                    {stats.checkedOut + stats.overdue !== 1 ? "s" : ""} checked out
                  </p>
                  {stats.overdue > 0 && (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      {stats.overdue} item{stats.overdue !== 1 ? "s" : ""} overdue
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => navigate("/my-books")}
                  >
                    View My Books
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              ) : (
                <div className="text-center py-6">
                  <BookOpen className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm font-medium text-slate-900">No items checked out</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Your borrowed items will appear here
                  </p>
                  {showCatalog && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => navigate("/catalog")}
                    >
                      <Search className="w-3.5 h-3.5 mr-1.5" />
                      Browse Catalog
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Explore the Library — always visible, fills space */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-slate-400" />
                Explore the Library
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {showCatalog && (
                  <ExploreItem
                    icon={<Search className="w-4 h-4" />}
                    label="Search Catalog"
                    description="Find books, journals, and more"
                    onClick={() => navigate("/catalog")}
                  />
                )}
                {showDigital && (
                  <ExploreItem
                    icon={<Monitor className="w-4 h-4" />}
                    label="Digital Resources"
                    description="E-books and digital content"
                    onClick={() => navigate("/catalog?type=digital")}
                  />
                )}
                {showHistory && (
                  <ExploreItem
                    icon={<Library className="w-4 h-4" />}
                    label="Reading History"
                    description="View your past checkouts"
                    onClick={() => navigate("/my-books")}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Reading Stats */}
          {showStats && (
            <Card className="border border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-slate-400" />
                  Reading Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <ReadingStat
                    label="Books This Year"
                    value={stats ? String(stats.booksThisYear) : "..."}
                  />
                  <ReadingStat
                    label="Total Borrowed"
                    value={stats ? String(stats.totalBorrowed) : "..."}
                  />
                  <ReadingStat label="Reviews Written" value="0" />
                  <ReadingStat label="Reading Lists" value="0" />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-5">
          {/* Quick Actions */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base font-semibold text-slate-900">
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {showCatalog && (
                  <QuickAction
                    icon={<Search className="w-4 h-4" />}
                    label="Search Catalog"
                    description="Search and discover books"
                    onClick={() => navigate("/catalog")}
                    highlighted
                  />
                )}
                {showHolds && (
                  <QuickAction
                    icon={<Clock className="w-4 h-4" />}
                    label="My Holds"
                    description="View and manage your holds"
                    onClick={() => navigate("/my-holds")}
                  />
                )}
                {showFines && (
                  <QuickAction
                    icon={<AlertTriangle className="w-4 h-4" />}
                    label="My Fines"
                    description="View and pay fines"
                    onClick={() => navigate("/my-fines")}
                  />
                )}
                <QuickAction
                  icon={<FileText className="w-4 h-4" />}
                  label="My Invoices"
                  description="View invoices and receipts"
                  onClick={() => navigate("/invoices")}
                />
                <QuickAction
                  icon={<User className="w-4 h-4" />}
                  label="My Account"
                  description="Update your profile and settings"
                  onClick={() => navigate("/profile")}
                />
              </div>
            </CardContent>
          </Card>

          {/* Account Summary */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base font-semibold text-slate-900">
                Account Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                <AccountRow label="Member Since" value={formatMemberSince(stats?.memberSince)} />
                <AccountRow
                  label="Membership Status"
                  value={
                    stats?.membershipStatus === "active"
                      ? "Active"
                      : (stats?.membershipStatus ?? "--")
                  }
                  valueColor={stats?.membershipStatus === "active" ? "text-emerald-600" : undefined}
                />
                <AccountRow
                  label="Outstanding Fines"
                  value={stats ? `₦${stats.pendingFinesAmount.toFixed(2)}` : "..."}
                  valueColor={stats && stats.pendingFinesAmount > 0 ? "text-amber-600" : undefined}
                />
                <AccountRow
                  label="Items on Hold"
                  value={stats ? String(stats.activeHolds) : "..."}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function StripMetric({
  label,
  value,
  icon,
  onClick,
  alert,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  onClick?: () => void;
  alert?: string;
}) {
  const content = (
    <>
      <div className="w-9 h-9 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-lg font-bold text-slate-900 leading-tight">{value}</p>
        <p className="text-xs text-slate-500 truncate">{label}</p>
        {alert && <p className="text-[10px] text-red-600 font-medium truncate">{alert}</p>}
      </div>
    </>
  );

  const baseClass =
    "flex items-center gap-3 p-4 text-left transition-colors border-b border-slate-100 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0";

  if (onClick) {
    return (
      <button onClick={onClick} className={`${baseClass} hover:bg-slate-50 cursor-pointer`}>
        {content}
      </button>
    );
  }

  return <div className={baseClass}>{content}</div>;
}

function ExploreItem({
  icon,
  label,
  description,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-primary/20 hover:bg-primary/5 transition-colors text-left cursor-pointer group"
    >
      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-500 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        <p className="text-[11px] text-slate-500 truncate">{description}</p>
      </div>
    </button>
  );
}

function ReadingStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center p-3 rounded-lg bg-primary/5">
      <p className="text-xl font-bold text-primary">{value}</p>
      <p className="text-xs text-slate-600 mt-0.5">{label}</p>
    </div>
  );
}

function AccountRow({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`text-sm font-medium ${valueColor || "text-slate-900"}`}>{value}</span>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  description,
  onClick,
  highlighted,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  highlighted?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left cursor-pointer group ${
        highlighted ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-slate-50"
      }`}
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
          highlighted
            ? "bg-primary/10 text-primary"
            : "bg-slate-100 text-slate-600 group-hover:bg-primary/5 group-hover:text-primary"
        }`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className={`text-sm font-medium ${highlighted ? "text-primary" : "text-slate-900"}`}>
          {label}
        </p>
        <p className={`text-xs ${highlighted ? "text-primary" : "text-slate-500"}`}>
          {description}
        </p>
      </div>
    </button>
  );
}
